import { MessageLoop, Message } from '@phosphor/messaging';
import { Widget } from '@phosphor/widgets';
import { DisposableCollection, Disposable } from './disposable';
import { Event, Emitter } from './event';
import PerfectScrollbar from 'perfect-scrollbar';
import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
import { debounce } from 'lodash-es';

export type MaybePromise<T> = T | PromiseLike<T>;

export const DEFAULT_SCROLL_OPTIONS: PerfectScrollbar.Options = {
  suppressScrollX: true,
  minScrollbarLength: 35,
};

/**
 * At a number of places in the code, we have effectively reimplemented Phosphor's Widget.attach and Widget.detach,
 * but omitted the checks that Phosphor expects to be performed for those operations. That is a bad idea, because it
 * means that we are telling widgets that they are attached or detached when not all the conditions that should apply
 * do apply. We should explicitly mark those locations so that we know where we should go fix them later.
 */
export namespace UnsafeWidgetUtilities {
  /**
   * Ordinarily, the following checks should be performed before detaching a widget:
   * It should not be the child of another widget
   * It should be attached and it should be a child of document.body
   */
  export function detach(widget: Widget): void {
    MessageLoop.sendMessage(widget, Widget.Msg.BeforeDetach);
    widget.node.remove();
    MessageLoop.sendMessage(widget, Widget.Msg.AfterDetach);
  };
  /**
   * @param ref The child of the host element to insert the widget before.
   * Ordinarily the following checks should be performed:
   * The widget should have no parent
   * The widget should not be attached, and its node should not be a child of document.body
   * The host should be a child of document.body
   * We often violate the last condition.
   */
  // eslint-disable-next-line no-null/no-null
  export function attach(widget: Widget, host: HTMLElement, ref: HTMLElement | null = null): void {
    MessageLoop.sendMessage(widget, Widget.Msg.BeforeAttach);
    host.insertBefore(widget.node, ref);
    MessageLoop.sendMessage(widget, Widget.Msg.AfterAttach);
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventListener<K extends keyof HTMLElementEventMap> = (this: HTMLElement, event: HTMLElementEventMap[K]) => any;
export interface EventListenerObject<K extends keyof HTMLElementEventMap> {
  handleEvent(evt: HTMLElementEventMap[K]): void;
}
export namespace EventListenerObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export function is<K extends keyof HTMLElementEventMap>(listener: any | undefined): listener is EventListenerObject<K> {
    return !!listener && 'handleEvent' in listener;
  }
}
export type EventListenerOrEventListenerObject<K extends keyof HTMLElementEventMap> = EventListener<K> | EventListenerObject<K>;
export function addEventListener<K extends keyof HTMLElementEventMap>(
  element: HTMLElement, type: K, listener: EventListenerOrEventListenerObject<K>, useCapture?: boolean
): Disposable {
  element.addEventListener(type, listener, useCapture);
  return Disposable.create(() =>
    element.removeEventListener(type, listener, useCapture)
  );
}

export class BaseWidget extends Widget {

  protected readonly onScrollYReachEndEmitter = new Emitter<void>();
  readonly onScrollYReachEnd: Event<void> = this.onScrollYReachEndEmitter.event;
  protected readonly onScrollUpEmitter = new Emitter<void>();
  readonly onScrollUp: Event<void> = this.onScrollUpEmitter.event;
  protected readonly onDidChangeVisibilityEmitter = new Emitter<boolean>();
  readonly onDidChangeVisibility = this.onDidChangeVisibilityEmitter.event;
  protected readonly onDidDisposeEmitter = new Emitter<void>();
  readonly onDidDispose = this.onDidDisposeEmitter.event;

  protected readonly toDispose = new DisposableCollection(
    this.onDidDisposeEmitter,
    Disposable.create(() => this.onDidDisposeEmitter.fire()),
    this.onScrollYReachEndEmitter,
    this.onScrollUpEmitter,
    this.onDidChangeVisibilityEmitter
  );
  protected readonly toDisposeOnDetach = new DisposableCollection();

  protected scrollBar: PerfectScrollbar;
  protected scrollOptions: PerfectScrollbar.Options;

  constructor() {
    super();
    this.debouncedResizeHandler = debounce(this.resizeHandler, 150, { trailing: true, maxWait: 1000 });
  }

  public setScrollOptions(options: PerfectScrollbar.Options) {
    this.scrollOptions = options;
  }

  private resizeHandler = () => {
    if (this.scrollBar) {
      this.scrollBar.update();
    }
  };
  private debouncedResizeHandler: () => void;

  dispose(): void {
    if (this.isDisposed) {
      return;
    }
    super.dispose();
    this.toDispose.dispose();
    (async () => {
      if (this.scrollOptions) {
        const container = await this.getScrollContainer();
        container && removeResizeListener(container, this.debouncedResizeHandler);
      }
    })();
  }

  protected onCloseRequest(msg: Message): void {
    super.onCloseRequest(msg);
    this.dispose();
  }

  protected onBeforeAttach(msg: Message): void {
    if (this.title.iconClass === '') {
      this.title.iconClass = 'no-icon';
    }
    super.onBeforeAttach(msg);
  }

  protected onAfterDetach(msg: Message): void {
    if (this.title.iconClass === 'no-icon') {
      this.title.iconClass = '';
    }
    super.onAfterDetach(msg);
  }

  protected onBeforeDetach(msg: Message): void {
    this.toDisposeOnDetach.dispose();
    super.onBeforeDetach(msg);
  }

  protected onAfterAttach(msg: Message): void {
    super.onAfterAttach(msg);
    if (this.scrollOptions) {
      (async () => {
        const container = await this.getScrollContainer();
        container && addResizeListener(container, this.debouncedResizeHandler);
        container.style.overflow = 'hidden';
        this.scrollBar = new PerfectScrollbar(container, this.scrollOptions);
        this.disableScrollBarFocus(container);
        this.toDisposeOnDetach.push(addEventListener(container, <any>'ps-y-reach-end', () => { this.onScrollYReachEndEmitter.fire(undefined); }));
        this.toDisposeOnDetach.push(addEventListener(container, <any>'ps-scroll-up', () => { this.onScrollUpEmitter.fire(undefined); }));
        this.toDisposeOnDetach.push(Disposable.create(() => {
          if (this.scrollBar) {
            this.scrollBar.destroy();
            this.scrollBar = undefined;
          }
          container.style.overflow = 'initial';
        }));
      })();
    }
  }

  protected getScrollContainer(): MaybePromise<HTMLElement> {
    return this.node;
  }

  protected disableScrollBarFocus(scrollContainer: HTMLElement): void {
    for (const thumbs of [scrollContainer.getElementsByClassName('ps__thumb-x'), scrollContainer.getElementsByClassName('ps__thumb-y')]) {
      for (let i = 0; i < thumbs.length; i++) {
        const element = thumbs.item(i);
        if (element) {
          element.removeAttribute('tabIndex');
        }
      }
    }
  }

  protected onUpdateRequest(msg: Message): void {
    super.onUpdateRequest(msg);
    if (this.scrollBar) {
      this.scrollBar.update();
    }
  }

  protected onResize(msg: Widget.ResizeMessage): void {
    super.onResize(msg);
    if (this.scrollBar) {
      this.scrollBar.update();
    }
  }

  protected addUpdateListener<K extends keyof HTMLElementEventMap>(element: HTMLElement, type: K, useCapture?: boolean): void {
    this.addEventListener(element, type, e => {
      this.update();
      e.preventDefault();
    }, useCapture);
  }

  protected addEventListener<K extends keyof HTMLElementEventMap>(element: HTMLElement, type: K, listener: EventListenerOrEventListenerObject<K>, useCapture?: boolean): void {
    this.toDisposeOnDetach.push(addEventListener(element, type, listener, useCapture));
  }

  setFlag(flag: Widget.Flag): void {
    super.setFlag(flag);
    if (flag === Widget.Flag.IsVisible) {
      this.onDidChangeVisibilityEmitter.fire(this.isVisible);
    }
  }

  clearFlag(flag: Widget.Flag): void {
    super.clearFlag(flag);
    if (flag === Widget.Flag.IsVisible) {
      this.onDidChangeVisibilityEmitter.fire(this.isVisible);
    }
  }

  public updateScrollBar(): void {
    if (this.scrollBar) {
      this.scrollBar.update();
    }
  }

}
